/**
 * The intake's attachments strip: photos or PDFs of the supplier's paper.
 *
 * States per file: pending (chosen on a document that has not been saved yet,
 * uploaded when it is), uploading, error (with retry or remove), and stored.
 * Stored files open through the short-lived URL the server signs; the bucket
 * is never public. Remove is offered on a draft only — the server refuses it
 * on a posted document — while adding stays open after posting, because the
 * paper often arrives after the goods. GRN/variants spec section 5.1.
 */
import React, {useRef} from 'react';
import {
  ATTACHMENT_TYPES,
  MAX_ATTACHMENTS,
  attachmentProblem,
  type IntakeAttachment,
} from '../../../internals/repository/StockIntakeRepository';

export type AttachmentItem =
  | {state: 'stored'; attachment: IntakeAttachment}
  | {state: 'pending' | 'uploading' | 'error'; id: string; file: File; error?: string};

interface Props {
  items: AttachmentItem[];
  /** False on a void document. */
  canAdd: boolean;
  /** True on a draft. */
  canRemove: boolean;
  onAdd: (files: File[]) => void;
  onRemove: (item: AttachmentItem) => void;
  onRetry: (item: AttachmentItem) => void;
}

const isImage = (type: string) => type.startsWith('image/');

const kb = (bytes: number) =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const IntakeAttachments = ({items, canAdd, canRemove, onAdd, onRemove, onRetry}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const room = MAX_ATTACHMENTS - items.length;

  return (
    <div className="mt-4 rounded-xl border border-borderc bg-surface p-4">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-text2">
          Attachments{' '}
          <span className="text-text3">
            ({items.length}/{MAX_ATTACHMENTS}) — delivery note or invoice, photo or PDF up to 10 MB
          </span>
        </span>
        {canAdd && room > 0 && (
          <>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="text-xs font-medium text-brand hover:opacity-80"
            >
              + Add file
            </button>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept={ATTACHMENT_TYPES.join(',')}
              className="hidden"
              data-testid="attachment-input"
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []).slice(0, room);
                if (files.length) onAdd(files);
                e.target.value = '';
              }}
            />
          </>
        )}
      </div>

      {items.length === 0 ? (
        <p className="mt-2 text-xs text-text3">No files attached.</p>
      ) : (
        <ul className="mt-3 flex flex-wrap gap-2">
          {items.map((item) => {
            const stored = item.state === 'stored' ? item.attachment : null;
            const name = stored ? stored.fileName : (item as any).file.name;
            const type = stored ? stored.contentType : (item as any).file.type;
            const size = stored ? stored.size : (item as any).file.size;
            const key = stored ? stored.attachmentId : (item as any).id;
            return (
              <li
                key={key}
                data-state={item.state}
                className={`flex items-center gap-2 rounded-lg border px-2 py-1.5 text-xs ${
                  item.state === 'error' ? 'border-red-300 bg-red-50' : 'border-borderc bg-surface'
                }`}
              >
                {stored?.url && isImage(type) ? (
                  <img src={stored.url} alt="" className="h-8 w-8 rounded object-cover" />
                ) : (
                  <span className="flex h-8 w-8 items-center justify-center rounded bg-surface2 text-[10px] font-semibold text-text2">
                    {isImage(type) ? 'IMG' : 'PDF'}
                  </span>
                )}
                <span className="max-w-[160px]">
                  {stored?.url ? (
                    <a
                      href={stored.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block truncate font-medium text-text1 hover:text-brand"
                    >
                      {name}
                    </a>
                  ) : (
                    <span className="block truncate font-medium text-text1">{name}</span>
                  )}
                  <span className="block text-text3">
                    {item.state === 'uploading' && 'Uploading…'}
                    {item.state === 'pending' && 'Uploads when saved'}
                    {item.state === 'error' && (item.error || 'Upload failed')}
                    {item.state === 'stored' && kb(size)}
                  </span>
                </span>
                {item.state === 'error' && (
                  <button
                    type="button"
                    onClick={() => onRetry(item)}
                    className="font-medium text-brand hover:opacity-80"
                  >
                    Retry
                  </button>
                )}
                {(item.state !== 'stored' || canRemove) && item.state !== 'uploading' && (
                  <button
                    type="button"
                    title="Remove"
                    onClick={() => onRemove(item)}
                    className="text-text2 hover:text-red-600"
                  >
                    ✕
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

/** Files the user chose, checked against the limits before any upload. */
export function toPendingItems(files: File[], makeId: () => string): AttachmentItem[] {
  return files.map((file) => {
    const problem = attachmentProblem(file);
    return problem
      ? {state: 'error' as const, id: makeId(), file, error: problem}
      : {state: 'pending' as const, id: makeId(), file};
  });
}

export default IntakeAttachments;
